import { clearAppToken, getAppToken } from "./auth";
import type {
  AuthConfigResponse,
  AuthExchangeResponse,
  CreditBalanceResponse,
  JobAcceptedResponse,
  JobResultResponse,
  JobStatusResponse,
  MeResponse,
  OptimizeRequest,
} from "./types";

export class ApiError extends Error {
  status: number;
  detail: unknown;

  constructor(status: number, detail: unknown) {
    super(typeof detail === "string" ? detail : `HTTP ${status}`);
    this.status = status;
    this.detail = detail;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAppToken();
  const headers = new Headers(init?.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (init?.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(path, { ...init, headers });

  if (response.status === 401) {
    clearAppToken();
  }

  if (!response.ok) {
    let detail: unknown = await response.text();
    try {
      detail = JSON.parse(detail as string);
      if (
        typeof detail === "object" &&
        detail !== null &&
        "detail" in (detail as Record<string, unknown>)
      ) {
        detail = (detail as Record<string, unknown>).detail;
      }
    } catch {
      // leave as text
    }
    throw new ApiError(response.status, detail);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export const api = {
  getAuthConfig: () => request<AuthConfigResponse>("/auth/config"),
  exchangeEveCode: (code: string) =>
    request<AuthExchangeResponse>("/auth/eve/code", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  me: () => request<MeResponse>("/auth/me"),
  getCredits: () => request<CreditBalanceResponse>("/api/credits"),
  createJob: (req: OptimizeRequest) =>
    request<JobAcceptedResponse>("/api/jobs", {
      method: "POST",
      body: JSON.stringify(req),
    }),
  getJobStatus: (jobId: string) =>
    request<JobStatusResponse>(`/api/jobs/${jobId}`),
  getJobResult: (jobId: string) =>
    request<JobResultResponse>(`/api/jobs/${jobId}/result`),
};
